import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithRefreshTokenHandler } from "../authFetchBaseQuery";
import { API_GET_DEALER_TRANSACTIONS, API_VOID_TRANSACTION } from "../ApiConstants";
import {
  IVoidTransactionRequest,
  IVoidTransactionResponse,
} from "../../types/types";
import { TransactionRequest, TransactionResponseWithTotals, TransactionsResponse } from "../../domain/transactions/type";

/**
 * RTK Query API slice for managing transaction-related API calls.
 *
 * Provides endpoints for:
 * - Fetching dealer transactions (paged and filtered).
 * - Voiding an existing transaction.
 * - Fetching transactions with advanced filters.
 */
export const transactionApi = createApi({
  /** Unique key for this API slice in the Redux store */
  reducerPath: "transactionApi",

  /** Custom base query handler with refresh token support */
  baseQuery: baseQueryWithRefreshTokenHandler,

  /** Tags for cache invalidation */
  tagTypes: ["transactions"],

  /** Ensures data refetch on component mount or when arguments change */
  refetchOnMountOrArgChange: true,

  /** Disables caching by default (data removed immediately when unused) */
  keepUnusedDataFor: 0,

  endpoints: (builder) => ({
    /**
     * Voids a transaction (e.g., refund or cancellation).
     *
     * @param body - Transaction details required for voiding.
     *
     * @returns The API response containing the result of the void operation.
     *
     * @remarks
     * Invalidates the `transactions` tag, causing related queries to refetch.
     */
    voidTransaction: builder.mutation<IVoidTransactionResponse, IVoidTransactionRequest>({
      query: (body: IVoidTransactionRequest) => ({
        url: API_VOID_TRANSACTION,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["transactions"],
    }),

    /**
     * Fetches transactions using a detailed request payload.
     *
     * @param payload - Contains filtering, pagination, and sorting options.
     *
     * @returns A paginated list of transactions matching the request criteria.
     *
     * @remarks
     * Provides the `transactions` tag for cache management.
     */
    getPayments: builder.query<TransactionResponseWithTotals, TransactionRequest>({
      query: (body) => ({
        url: 'Transaction/GetPayments',
        method: "POST",
        body,
      }),
      providesTags: ["transactions"],
    }),

    /**
     * Fetches transactions using a detailed request payload.
     *
     * @param payload - Contains filtering, pagination, and sorting options.
     *
     * @returns A paginated list of transactions matching the request criteria.
     *
     */
    getAllPaymentsToExport: builder.query<TransactionResponseWithTotals, TransactionRequest>({
      query: (body) => ({
        url: 'Transaction/GetPayments',
        method: "POST",
        body,
      }),
    }),
  }),
});

/** Hook for fetching dealer transactions */
export const {
  useVoidTransactionMutation,
  useGetPaymentsQuery,
  useLazyGetAllPaymentsToExportQuery
} =
  transactionApi;
